import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import DataTableManual from "components/Common/DataTableManual/DataTableManual";

import { Button } from "react-bootstrap";

import ReactTimeAgo from 'react-time-ago';

import Alert from "components/Common/Alert";
import { Tooltip } from "components/Common/Tooltip/";
import Icon from "components/Icons/Icon";

import conxtdOut from "apis/conxtdOut";

import { loadSitesOnTestStart } from "services/redux/actions/sitesOnTest";

import { getEnterpriseFromRoute } from "services/redux/selectors/enterprises";
import { getGroupFromRoute, getGroupById, getGroups, getCustomRef } from "services/redux/selectors/groups";
import { getSitesOnTestSites } from "services/redux/selectors/sitesOnTest";
import { createGetEventMetaById } from "services/redux/selectors/eventMeta";

import SiteNameCell from "components/Common/DataTable/SiteNameCell/";
import {
  SiteGroup,
  SiteStatus,
  SiteOnTestEventCell,
  InnerColourCell,
  RightColourCell,
} from "../Cells/";

import SiteView from "components/Site/SiteView/";

import Loading from "components/Loading";

import { exact } from "services/filters/";

import moment from "services/locale/momentInit.js";

import isEmpty from "lodash/isEmpty";
import get from "lodash/get";

import COLOUR_CODES from "constants/COLOUR_CODES.json";

function DataTable(props) {
  const dispatch = useDispatch();

  const [showSidePane, setShowSidePane] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [bundleId, setBundleId] = useState(2);
  const [buttonLoadingIndex, setButtonLoadingIndex] = useState(null);

  const enterprise = useSelector(getEnterpriseFromRoute);
  const group = useSelector(getGroupFromRoute);
  const groups = useSelector(getGroups);
  const sitesOnTest = useSelector(getSitesOnTestSites);
  const pagination = useSelector(state => state.sitesOnTest.data.pagination);
  const sortField = useSelector(state => state.sitesOnTest.sortField);
  const sortDirection = useSelector(state => state.sitesOnTest.sortDirection);
  const filters = useSelector(state => state.sitesOnTest.filters);
  const loadingSitesOnTest = useSelector(state => state.sitesOnTest.loadingSitesOnTest);
  const getEventMetaById = useSelector(createGetEventMetaById);

  const enterpriseId = enterprise.id;
  const groupId = (group && group.id) || enterprise.root_group_id;

  const onSelectSite = site => {
    if (site && site !== selectedSite) {
      // New site selected
      document.body.classList.add("sidepane-active");

      setShowSidePane(true);
      setSelectedSite(site);
      setBundleId(null);
    } else {
      // Clicked a site that's already being viewed, close
      document.body.classList.remove("sidepane-active");

      // Delay by 600ms to make sidepane exit gracefully
      setTimeout(() => {
        setShowSidePane(false);
        setSelectedSite(null);
        setBundleId(null);
      }, 600);
    }
  };

  const columns = [
    {
      key: "CUSTOM_REF",
      Header: "Ref",
      id: "custom_ref",
      className: "text-center",
      width: 60,
      filterable: false,
      sortable: false,
      accessor: (row) => {
        return getCustomRef({
          custom_ref: row.custom_ref || "",
          name: row.site_name || "",
        });
      },
    },
    {
      key: "SITE_NAME",
      Header: "Site Name",
      accessor: "site_name",
      id: "site_name",
      className: "site-name",
      Cell: (row) => {
        if (!isEmpty(row.original.site)) { 
          return (
            <SiteNameCell
              value={row.original.site_name}
              selectSite={() => {
                onSelectSite(row.original.site);
              }}
            />
          )
        }
      },
    },
    {
      key: "SITE_REF",
      Header: "Site Ref",
      id: "site_ref",
      width: 80,
      accessor: "site_ref",
    },
    {
      key: "SITE_GROUP",
      Header: "Group",
      id: "group_name",
      filterable: false,
      sortable: false,
      className: "text-center",
      width: 65,
      accessor: (row) => {
        if (row.custom_group_id || row.group_name) {
          return {
            custom_group_ref: row.custom_group_id,
            name: row.group_name,
            site_group_custom_ref: getCustomRef({ custom_group_ref: row.custom_group_id, name: row.group_name}),
          }
        } else if (row.site && row.site.enterprise_groups && row.site.enterprise_groups.length > 0) {
          const siteGroup = getGroupById(groups, row.site.enterprise_groups[0].id);  // use the first group in the list, which should be the lowest in the group tree
          return {
            ...siteGroup,
            site_group_custom_ref: siteGroup ? getCustomRef({ custom_group_ref: siteGroup.custom_group_id, name: siteGroup.name}) : "",
          }
        } else {
          return null;
        }
      },
      Cell: ({row}) => {
        const {group_name} = row;
        if (group_name) {
          return (
            <SiteGroup
              siteGroup={group_name}
              placement="left"
            />
          )
        } else {
          return <></>
        }
      },
      filterMethod: (filter, row) => {
        // look for the filter string in both site group custom ref and name
        return (
          String(row[filter.id].site_group_custom_ref)
            .toLowerCase()
            .indexOf(filter.value.toLowerCase()) !== -1
          ||
          String(row[filter.id].name)
            .toLowerCase()
            .indexOf(filter.value.toLowerCase()) !== -1
        );
      },
      sortMethod: (a, b) => {
        // sort by site group custom ref
        const customRefA = String(a.site_group_custom_ref).toLowerCase();
        const customRefB = String(b.site_group_custom_ref).toLowerCase();
        const compareCustomRef = customRefA.localeCompare(customRefB);
        if (compareCustomRef !== 0) {
          return compareCustomRef;
        } else {
          // if site group custom refs are the same, sort by site group name
          const groupNameA = String(a.name).toLowerCase();
          const groupNameB = String(b.name).toLowerCase();
          return groupNameA.localeCompare(groupNameB);
        }
      },
    },
    {
      key: "SITE_STATUS",
      Header: "Site Status",
      headerClassName: "wordwrap",
      id: "open_close_state",
      accessor: "open_close_state",
      className: "site-status, text-center",
      filterable: false,
      width: 90,
      Cell: (row) => {
        return (
          <SiteStatus
            {...row} 
          />
        )
      },
      filterMethod: (filter, row) => {
        let rowValue = null;
        rowValue = row[filter.id] && String(row[filter.id]).toLowerCase();
        if (rowValue === "o") {
          return (
            exact({
              filter: filter.value,
              value: String("open"),
              ignoreCase: true,
            })
            ||
            exact({
              filter: filter.value,
              value: String("unlock"),
              ignoreCase: true,
            })
          );
        } else if (rowValue === "c") {
          return (
            exact({
              filter: filter.value,
              value: String("close"),
              ignoreCase: true,
            })
            ||
            exact({
              filter: filter.value,
              value: String("lock"),
              ignoreCase: true,
            })
          );
        } else if (!rowValue) {
          return exact({
            filter: filter.value,
            value: String("unknown"),
            ignoreCase: true,
          })
        } else {
          return false;
        }
      },
    },
    {
      key: "TEST_START",
      Header: "Test Start",
      id: "time_in",
      width: 90,
      className: "alarm-colour-cell text-right",
      filterable: false,
      accessor: "time_in",
      Cell: ({original}) => {
        return (
          <SiteOnTestEventCell
            event={getEventMetaById(original.alarm_event_id)}
            overrideColour={original.time_out_warning && COLOUR_CODES["alertOrange"]}
            date={original.time_in}
            timezone={null}
            alarm_text={original.alarm_text}
            focus={true}
          />
        )
      },
    },
    {
      key: "TEST_END",
      Header: "Test End",
      id: "time_out",
      width: 130,
      className: "text-right alarm-colour-cell",
      filterable: false,
      accessor: "time_out",
      Cell: ({original}) => {
        const timeOutDate = new moment(original.time_out);
        const localTimezoneText = moment.tz.guess();

        return (
          <InnerColourCell
            backgroundColour={original.time_out_warning ? COLOUR_CODES["alertOrange"] : getEventMetaById(original.alarm_event_id).colour}
            displayValue={
              timeOutDate.isValid()
                ? <div className="d-flex justify-content-between align-items-center">
                    {original.time_out_warning ? <span><Icon className="ml-2 mr-2 fas fa-clock" /></span> : <span></span>}
                    <ReactTimeAgo 
                      date={timeOutDate.valueOf()}
                      locale="en-GB"
                      timeStyle="round"
                      tooltip={false}
                      wrapperComponent={({ children }) => {
                        return <Tooltip 
                          description={`${timeOutDate.format("HH:mm DD/MM/YYYY")} ${localTimezoneText}`}
                          placement="top"
                        >{children}</Tooltip>
                      }}
                    />
                  </div>
                : "Invalid date"
            }
            focus={true}
            style={{ paddingRight: "10px" }}
          />
        )
      },
    },
    {
      key: "EVENT_COUNT",
      Header: "Events",
      id: "number_of_events",
      width: 70,
      className: "text-right alarm-colour-cell pr-1",
      filterable: false,
      accessor: "number_of_events",
      Cell: ({original}) => {
        return (
          <RightColourCell
            backgroundColour={original.time_out_warning ? COLOUR_CODES["alertOrange"] : getEventMetaById(original.alarm_event_id).colour}
            displayValue={String(original.number_of_events)}
            focus={true}
            style={{ paddingRight: "12px" }}
          />
        )
      }
    },
    {
      key: "ACTIONS",
      Header: "Actions",
      className: "actions",
      width: 100,
      filterable: false,
      sortable: false,
      Cell: ( row ) => {
        return (
          <Button
            className="inner"
            variant="success"
            onClick={() => {
              setButtonLoadingIndex(row.index);
              // Alert({
              //   text: "Enter Password",
              //   input: 'password',
              //   // icon: "warning",
              //   showCancelButton: true,
              //   showConfirmButton: true,
              // }).then((result) => {
              //   if (result.isConfirmed) {
                  const takeSiteOffTest = conxtdOut.post(`/EnterpriseSites/enableTestMode/${row.original.enterprise_site_id}`, {
                    toggle: "dis",
                    // mode_password: result.value,
                  });
        
                  takeSiteOffTest.then(() => {
                    dispatch(
                      loadSitesOnTestStart({ 
                        enterprise_id: enterpriseId,
                        group_id: groupId,
                        pageSize: pagination.perPage,
                        requestPageNumber: pagination.requestPage,
                        sortField: sortField,
                        sortDirection: sortDirection,
                        filters: filters,
                      })
                    );

                    setButtonLoadingIndex(null);

                    Alert({
                      text: "Site has been taken off test.",
                      icon: "success",
                      timerProgressBar: true,
                      timer: 3000,
                    });
                    
                  }).catch((error) => {
                    setButtonLoadingIndex(null);

                    const status = get(error.response, "status");
                    
                    let messageText = "Issue taking site off test. Please try again later.";
                    // 422 unprocessable
                    if (status === 422 && error.response.data && error.response.data.message) {
                      messageText = error.response.data.message;
                    }
        
                    Alert({
                      text: messageText,
                      icon: "warning",
                      showConfirmButton: true,
                    });
                  });
              //   }
              // });
            }}
          >{ buttonLoadingIndex === row.index ? <Loading center /> : "End Test" }</Button>
        )
      },
    },
  ];

  const loadingMsg = loadingSitesOnTest ? <Loading center /> : "No data available...";

  return (
    <div>
      <div className="card visual-data in-alarm-list">
        <DataTableManual
          columns={columns}
          data={(sitesOnTest && sitesOnTest.length > 0) ? sitesOnTest : []}
          defaultPageSize={20}
          noDataText={loadingMsg}
          loadDataAction={loadSitesOnTestStart}
          loadDataParams={{
            enterprise_id: enterpriseId,
            group_id: groupId,
          }}
          pages={pagination && pagination.pageCount}
          sortable={sitesOnTest.length > 0}
          multiSort={false}
          defaultSorted={[
            {
              id: "time_out",
              desc: false,
            },
          ]}
          filterable={true}
        />
      </div>
      <SiteView
        site={selectedSite}
        selectSite={onSelectSite}
        bundle_id={bundleId}
        openOnView="modes"
        openOnTab="test"
        showSidePane={showSidePane}
        onSelectBundle={bundleId => {
          // Bundle selected
          if (bundleId) {
            setBundleId(bundleId)
          }
          // Invalid bundle id, deselect
          else {
            setBundleId(null)
          }
        }}
      />
    </div>
  );
}

export default DataTable;